<template>
  <div
    class="ttfb-outer"
    :class="{ 'theme--dark': dark }"
    @mousedown="handleDragStart"
    @mousemove="handleMouseMove"
    @mouseup="handleDragEnd"
    @mouseleave="handleMouseLeave"
    @touchstart.passive="handleTouchStart"
    @touchmove.passive="handleTouchMove"
    @touchend="handleDragEnd"
  >
    <div
      v-if="hoverInfo && hoverInfo.seated + hoverInfo.incoming > 0"
      class="ttfb-tooltip"
      :style="{ left: hoverInfo.x + 'px', top: hoverInfo.y + 'px' }"
    >
      <div>
        <span class="ttfb-tooltip__time">{{ hoverInfo.time }}</span>
      </div>
      <v-divider
        v-if="hoverInfo.seated > 0 && hoverInfo.incoming > 0"
        class="mx-1 my-0 py-0"
        inset
        vertical
      />
      <div>
        <span
          v-if="hoverInfo.incoming > 0"
          class="ttfb-tooltip__row"
        >
          <span class="ttfb-tooltip__dot ttfb-tooltip__dot--incoming" />
          {{ $t('label.incoming_guests') }}: {{ hoverInfo.incoming }}
        </span>
        <span class="ttfb-tooltip__row">
          <span class="ttfb-tooltip__dot ttfb-tooltip__dot--seated" />
          {{ $t('label.seated_guests') }}: {{ hoverInfo.seated }}
        </span>
      </div>
    </div>
    <svg
      v-if="slotData.length"
      class="ttfb-svg"
      width="100%"
      height="40"
      :viewBox="`0 0 ${dimTotalWidth} 40`"
      preserveAspectRatio="none"
    >

      <!-- seated -->
      <path
        :d="seatedPath"
        class="ttfb-area--seated"
      />
      <path
        :d="seatedLine"
        class="ttfb-line--seated"
      />
      <!-- incoming -->
      <path
        v-for="(bar, i) in arrivalBars"
        :key="'a' + i"
        :d="bar.d"
        class="ttfb-bar--incoming"
      />
      <!-- dim overlay outside the visible viewport When scrolled covers 0..asideWidth+scrollOffset -->
      <rect
        v-if="containerWidth > 0 && scrollOffset > 0"
        x="0"
        y="0"
        :width="asideWidth + scrollOffset"
        height="40"
        class="ttfb-dim"
      />
      <rect
        v-if="containerWidth > 0"
        :x="scrollOffset + containerWidth"
        y="0"
        :width="Math.max(0, dimTotalWidth - scrollOffset - containerWidth)"
        height="40"
        class="ttfb-dim"
      />
    </svg>
  </div>
</template>

<script lang="ts" setup>
import {
  computed, ref, watch, onMounted, nextTick,
} from 'vue';
import Reservation from '@/model/Reservation';
import TimeSlot from '@/model/TimeSlot';
import { useVuetify } from '@/components/helpers/vue2-helpers';
import { niceTimeTextFromSlot } from '@/services/time-utils';

const props = withDefaults(defineProps<{
  timeSlots: TimeSlot[];
  reservations: Reservation[];
  slotWidth: number;
  asideWidth: number;
  scrollOffset?: number;
  containerWidth?: number;
}>(), {
  scrollOffset: 0,
  containerWidth: 0,
});

const BOTTOM = 35;
const TOP = 5;
const RANGE = BOTTOM - TOP;
const BAR_RADIUS = 3;

const quarterSlotWidth = computed(() => props.slotWidth / 2);

const buildPoints = (values: number[], maxV: number): [number, number][] => values.map((v, i) => [
  props.asideWidth + (i + 0.5) * quarterSlotWidth.value,
  v > 0 ? BOTTOM - Math.round((v / maxV) * RANGE) : BOTTOM,
] as [number, number]);

const buildCurvePath = (pts: [number, number][]): string => {
  if (pts.length < 2) return '';
  let d = `M ${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const cpx = (pts[i][0] + pts[i + 1][0]) / 2;
    d += ` C ${cpx},${pts[i][1]} ${cpx},${pts[i + 1][1]} ${pts[i + 1][0]},${pts[i + 1][1]}`;
  }
  return d;
};

const vuetify = useVuetify();

const dark = computed(() => vuetify.theme.dark);

type SlotGuestData = { seated: number; incoming: number; time: string };

const countGuestsAtSlot = (guests: Reservation[], slot: number): SlotGuestData => {
  let seated = 0;
  let incoming = 0;
  guests.forEach((r) => {
    if (r.slotBegin === slot) incoming += r.partySize;
    if (r.slotBegin <= slot && r.slotEnd >= slot) seated += r.partySize;
  });
  return { seated, incoming, time: niceTimeTextFromSlot(slot) };
};

const slotData = computed((): SlotGuestData[] => {
  const guests = props.reservations.filter((r) => r.partySize > 0);
  // Each grid column spans 30 min; count guests at its two 15-min slots.
  return props.timeSlots.flatMap((ts) => [
    countGuestsAtSlot(guests, ts.slot),
    countGuestsAtSlot(guests, ts.slot + 1),
  ]);
});

const maxVal = computed(() => Math.max(...slotData.value.map((s) => s.seated), 1));

const totalWidth = computed(() => props.asideWidth + props.timeSlots.length * props.slotWidth);

const seatedLine = computed(() => buildCurvePath(
  buildPoints(slotData.value.map((s) => s.seated), maxVal.value),
));

const seatedPath = computed(() => {
  const pts = buildPoints(slotData.value.map((s) => s.seated), maxVal.value);
  if (pts.length < 2) return '';
  return `${buildCurvePath(pts)} L ${pts[pts.length - 1][0]},36 L ${pts[0][0]},36 Z`;
});

const arrivalBars = computed(() => {
  const barW = Math.max(2, Math.round(quarterSlotWidth.value * 0.6));
  const maxV = maxVal.value;
  return slotData.value.reduce<{ d: string }[]>((acc, s, i) => {
    if (s.incoming <= 0) return acc;
    const cx = props.asideWidth + (i + 0.5) * quarterSlotWidth.value;
    const h = Math.max(1, Math.round((s.incoming / maxV) * RANGE));
    const x = Math.round(cx - barW / 2);
    const x2 = x + barW;
    const y = BOTTOM - h;
    const r = Math.min(BAR_RADIUS, h / 2, barW / 2);
    const d = r < 1
      ? `M ${x},${BOTTOM} L ${x},${y} L ${x2},${y} L ${x2},${BOTTOM} Z`
      : `M ${x},${BOTTOM} L ${x},${y + r} Q ${x},${y} ${x + r},${y}`
        + ` L ${x2 - r},${y} Q ${x2},${y} ${x2},${y + r} L ${x2},${BOTTOM} Z`;
    acc.push({ d });
    return acc;
  }, []);
});

const dragging = {
  active: false, dragged: false, anchorClientX: 0, anchorScrollLeft: 0,
};

const getScrollEl = () => document.getElementById('tt-scrollable-container');

const actualScrollWidth = ref(0);
const updateActualScrollWidth = () => {
  const el = getScrollEl();
  if (el) actualScrollWidth.value = el.scrollWidth;
};

watch([() => props.slotWidth, () => props.timeSlots.length], () => nextTick(updateActualScrollWidth));
onMounted(() => nextTick(updateActualScrollWidth));

const dimTotalWidth = computed(() => actualScrollWidth.value || totalWidth.value);

// ─── Hover tooltip ───────────────────────────────────────────────────────────
const hoverInfo = ref<{
  x: number; y: number; time: string; seated: number; incoming: number;
} | null>(null);

let hoverTimer: ReturnType<typeof setTimeout> | null = null;
let pendingSlotIdx = -1;

const clearHoverTimer = () => {
  if (hoverTimer !== null) { clearTimeout(hoverTimer); hoverTimer = null; }
};

const getSlotAtClientX = (clientX: number, rect: DOMRect): number => {
  const contentX = ((clientX - rect.left) / rect.width) * dimTotalWidth.value;
  return Math.floor((contentX - props.asideWidth) / quarterSlotWidth.value);
};

// ─── Drag ────────────────────────────────────────────────────────────────────
const handleDragStart = (e: MouseEvent) => {
  dragging.active = true;
  dragging.dragged = false;
  dragging.anchorClientX = e.clientX;
  dragging.anchorScrollLeft = getScrollEl()?.scrollLeft ?? 0;
};

const handleDragMove = (e: MouseEvent) => {
  if (!dragging.active || e.buttons === 0) { dragging.active = false; return; }
  const delta = e.clientX - dragging.anchorClientX;
  if (Math.abs(delta) < 3) return; // ignore tiny jitter
  dragging.dragged = true;
  const scrollEl = getScrollEl();
  if (!scrollEl) return;
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const deltaContent = (delta / rect.width) * dimTotalWidth.value;
  scrollEl.scrollLeft = Math.max(0, dragging.anchorScrollLeft + deltaContent);
};

const handleDragEnd = (e: MouseEvent | TouchEvent) => {
  if (dragging.active && !dragging.dragged) {
    // plain click/tap — center on the clicked position
    const scrollEl = getScrollEl();
    if (scrollEl) {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const contentX = ((dragging.anchorClientX - rect.left) / rect.width) * dimTotalWidth.value;
      scrollEl.scrollLeft = Math.max(0, contentX - scrollEl.clientWidth / 2);
    }
  }
  dragging.active = false;
  dragging.dragged = false;
};

// ─── Mouse move / leave (tooltip + drag proxy) ───────────────────────────────
const handleMouseMove = (e: MouseEvent) => {
  if (dragging.active) {
    handleDragMove(e);
    hoverInfo.value = null;
    clearHoverTimer();
    pendingSlotIdx = -1;
    return;
  }
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const idx = getSlotAtClientX(e.clientX, rect);
  const slot = slotData.value[idx];
  if (!slot) {
    hoverInfo.value = null;
    clearHoverTimer();
    pendingSlotIdx = -1;
    return;
  }

  const x = e.clientX + 14;
  const y = e.clientY - 36;

  if (hoverInfo.value) {
    hoverInfo.value = {
      x, y, time: slot.time, seated: slot.seated, incoming: slot.incoming,
    };
    return;
  }

  if (idx !== pendingSlotIdx) {
    pendingSlotIdx = idx;
    clearHoverTimer();
    hoverTimer = setTimeout(() => {
      hoverInfo.value = {
        x, y, time: slot.time, seated: slot.seated, incoming: slot.incoming,
      };
      hoverTimer = null;
    }, 400);
  }
};

const handleMouseLeave = (e: MouseEvent) => {
  hoverInfo.value = null;
  clearHoverTimer();
  pendingSlotIdx = -1;
  handleDragEnd(e);
};

const handleTouchStart = (e: TouchEvent) => {
  dragging.active = true;
  dragging.dragged = false;
  dragging.anchorClientX = e.touches[0].clientX;
  dragging.anchorScrollLeft = getScrollEl()?.scrollLeft ?? 0;
};

const handleTouchMove = (e: TouchEvent) => {
  if (!dragging.active) return;
  const delta = e.touches[0].clientX - dragging.anchorClientX;
  if (Math.abs(delta) < 3) return;
  dragging.dragged = true;
  const scrollEl = getScrollEl();
  if (!scrollEl) return;
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const deltaContent = (delta / rect.width) * dimTotalWidth.value;
  scrollEl.scrollLeft = Math.max(0, dragging.anchorScrollLeft + deltaContent);
};
</script>

<style lang="scss" scoped>
$seated-color:    map-get($blue, 'lighten-2');
$incoming-color:  $blue-color;

// light theme
$bg-light:        #ffffff80;
$border-light:    $grey-15-color;

// dark theme
$bg-dark:         rgba($grey-92-color, 0.05);
$border-dark:     $grey-88-color;

// ─── Component ───────────────────────────────────────────────────────────────
.ttfb-outer {
  position: relative;
  height: 37px;
  overflow: hidden;
  border-top: 1px solid $border-light;
  background: $bg-light;
  flex-shrink: 0;
  cursor: pointer;

  // backdrop-filter is on a pseudo-element so .ttfb-outer itself does NOT
  // become a containing block, which would break position:fixed on the tooltip.
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    backdrop-filter: blur(8px);
    pointer-events: none;
    z-index: 0;
  }

  &.theme--dark {
    border-top-color: $border-dark;
    background: $bg-dark;
  }
}

.ttfb-svg {
  display: block;
  position: relative;
  z-index: 1;
}

.ttfb-area--seated {
  fill: rgba($seated-color, 0.15);

  .theme--dark & {
    fill: rgba($seated-color, 0.12);
  }
}

.ttfb-line--seated {
  fill: none;
  stroke: rgba($seated-color, 0.65);
  stroke-width: 1.5;
  stroke-linejoin: round;
  stroke-linecap: round;

  .theme--dark & {
    stroke: rgba($incoming-color, 0.85);
  }
}

.ttfb-bar--incoming {
  fill: rgba($incoming-color, 0.82);

  .theme--dark & {
    fill: rgba($incoming-color, 0.85);
  }
}

.ttfb-dim {
  fill: rgba(0, 0, 0, 0.1);
  pointer-events: none;

  .theme--dark & {
    fill: rgba(0, 0, 0, 0.45);
  }
}

// ─── Tooltip ─────────────────────────────────────────────────────────────────
.ttfb-tooltip {
  position: fixed;
  transform: none;
  background: rgba(30, 30, 30, 0.82);
  color: #fff;
  border-radius: 5px;
  padding: 2px 7px;
  font-size: 13px;
  line-height: 1.5;
  white-space: nowrap;
  pointer-events: none;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 200;

  .theme--dark & {
    background: rgba(60, 60, 60, 0.9);
  }
}

.ttfb-tooltip__time {
  font-weight: 600;
  opacity: 0.75;
}

.ttfb-tooltip__row {
  display: flex;
  align-items: center;
  gap: 3px;
}

.ttfb-tooltip__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &--seated {
    background: $seated-color;
  }

  &--incoming {
    background: $incoming-color;
  }
}
</style>
